import * as i0 from '@angular/core';
import { forwardRef, Directive, Attribute, Inject, Input, HostBinding, HostListener, NgModule } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { DOCUMENT } from '@angular/common';

class EditableDirective {
    constructor(elementRef, renderer, unformattedPaste, document) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.unformattedPaste = unformattedPaste;
        this.document = document;
        this.propValueAccessor = 'textContent';
        this.editable = true;
    }
    callOnChange() {
        if (typeof this.onChange == 'function') {
            this.onChange(this.elementRef.nativeElement[this.propValueAccessor]);
        }
    }
    callOnTouched() {
        if (typeof this.onTouched == 'function') {
            this.onTouched();
        }
    }
    /**
     * Writes a new value to the element.
     * This method will be called by the forms API to write
     * to the view when programmatic (model -> view) changes are requested.
     *
     * See: [ControlValueAccessor](https://angular.io/api/forms/ControlValueAccessor#members)
     */
    writeValue(value) {
        const normalizedValue = value == null ? '' : value;
        this.renderer.setProperty(this.elementRef.nativeElement, this.propValueAccessor, normalizedValue);
    }
    /**
     * Registers a callback function that should be called when
     * the control's value changes in the UI.
     *
     * This is called by the forms API on initialization so it can update
     * the form model when values propagate from the view (view -> model).
     */
    registerOnChange(fn) {
        this.onChange = fn;
    }
    /**
     * Registers a callback function that should be called when the control receives a blur event.
     * This is called by the forms API on initialization so it can update the form model on blur.
     */
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    /**
     * This function is called by the forms API when the control status changes to or from "DISABLED".
     * Depending on the value, it should enable or disable the appropriate DOM element.
     */
    setDisabledState(isDisabled) {
        if (isDisabled) {
            this.renderer.setAttribute(this.elementRef.nativeElement, 'disabled', 'true');
            this.removeDisabledState = this.renderer.listen(this.elementRef.nativeElement, 'keydown', this.listenerDisabledState);
        }
        else {
            if (this.removeDisabledState) {
                this.renderer.removeAttribute(this.elementRef.nativeElement, 'disabled');
                this.removeDisabledState();
            }
        }
    }
    preventFormatedPaste(event) {
        if (this.unformattedPaste === null ||
            this.unformattedPaste == 'false' ||
            !this.document.execCommand) {
            return;
        }
        event.preventDefault();
        const { clipboardData } = event;
        const text = clipboardData?.getData('text/plain') || clipboardData?.getData('text');
        this.document.execCommand('insertText', false, text);
    }
    listenerDisabledState(e) {
        e.preventDefault();
    }
}
EditableDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.0", ngImport: i0, type: EditableDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: 'unformattedPaste', attribute: true }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Directive });
EditableDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.0", type: EditableDirective, selector: "[editable][formControlName],[editable][formControl],[editable][ngModel]", inputs: { propValueAccessor: "propValueAccessor", editable: "editable" }, host: { listeners: { "input": "callOnChange()", "blur": "callOnTouched()", "paste": "preventFormatedPaste($event)" }, properties: { "attr.contenteditable": "this.editable" } }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EditableDirective),
            multi: true,
        },
    ], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.0", ngImport: i0, type: EditableDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[editable][formControlName],[editable][formControl],[editable][ngModel]',
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => EditableDirective),
                            multi: true,
                        },
                    ],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: undefined, decorators: [{
                    type: Attribute,
                    args: ['unformattedPaste']
                }] }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; }, propDecorators: { propValueAccessor: [{
                type: Input
            }], editable: [{
                type: HostBinding,
                args: ['attr.contenteditable']
            }, {
                type: Input
            }], callOnChange: [{
                type: HostListener,
                args: ['input']
            }], callOnTouched: [{
                type: HostListener,
                args: ['blur']
            }], preventFormatedPaste: [{
                type: HostListener,
                args: ['paste', ['$event']]
            }] } });

class NgsContenteditableModule {
}
NgsContenteditableModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.0", ngImport: i0, type: NgsContenteditableModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NgsContenteditableModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.0", ngImport: i0, type: NgsContenteditableModule, declarations: [EditableDirective], exports: [EditableDirective] });
NgsContenteditableModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.0", ngImport: i0, type: NgsContenteditableModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.0", ngImport: i0, type: NgsContenteditableModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [EditableDirective],
                    exports: [EditableDirective],
                }]
        }] });

/*
 * Public API Surface of contenteditable
 */

/**
 * Generated bundle index. Do not edit.
 */

export { EditableDirective, NgsContenteditableModule };
